<script setup lang="ts">
import { computed } from 'vue';

const model = defineModel<String>()
const { canSend, minLen = -1, maxLen = -1, placeholder = "", testId = "message" } = defineProps<{
    canSend: boolean,
    minLen?: number,
    maxLen?: number,
    placeholder?: string,
    // Prefix of the `data-testid` hooks (`<testId>-input`, `<testId>-send`) so the e2e suite
    // can tell the chat box apart from the support one.
    testId?: string,
}>()

const events = defineEmits([
    'send'
])

// Length is measured on the trimmed value so the counter agrees with what
// actually gets validated and sent.
const len = computed(() => (model.value ?? '').trim().length)

const lengthOk = computed(() => {
    if (!model.value) {
        return false
    }

    if (maxLen < 0 && minLen < 0) {
        return true
    }

    const s = len.value

    const low = s >= minLen
    const high = s <= maxLen

    if (maxLen < 0) {
        return low
    }
    else if (minLen < 0) {
        return high
    }

    return low && high
})

// Non-intrusive feedback: stays hidden on an empty field, shows a discreet
// character count once typing starts, and only changes tone when a limit is
// crossed so the disabled send button is never unexplained.
const counter = computed(() => {
    const hasMax = maxLen >= 0
    const hasMin = minLen > 0

    if ((!hasMax && !hasMin) || len.value === 0) {
        return null
    }

    if (hasMin && len.value < minLen) {
        return { text: `Minimo ${minLen} caratteri`, tone: 'under' }
    }

    if (hasMax && len.value > maxLen) {
        return { text: `${len.value}/${maxLen}`, tone: 'over' }
    }

    return { text: hasMax ? `${len.value}/${maxLen}` : `${len.value}`, tone: 'ok' }
})

// Both limit breaches read as an error; only going over the maximum also reddens the field
// border, since that's the one where the offending text is already in the box.
const counterClass = computed(() =>
    counter.value && counter.value.tone !== 'ok' ? 'text-error' : 'text-gray'
)

const enabled = computed(() => {
    return canSend && lengthOk.value
})

function send() {
    if (!enabled.value) {
        return
    }

    model.value = model.value.trim()
    events('send')
}
</script>

<template>
    <div class="col gap-1.5">
        <form class="flex gap-2 min-h-12" @submit.prevent="send">
            <!-- The border is the focus indicator, so the input inside drops its own ring -->
            <div class="border-4 flex-1 rnd p-2 row items-center gap-2 relative"
                :class="counter?.tone === 'over' ? 'border-error' : 'border-gray-300 focus-within:border-blue'">
                <slot name="before-input"></slot>
                <input v-model="model" class="flex-1 min-w-0 outline-none" :disabled="!canSend" :placeholder="placeholder" 
                    :data-testid="`${testId}-input`" />
                <slot name="after-input"></slot>
                <button type="submit" :disabled="!enabled" class="send-btn" title="Invia" :data-testid="`${testId}-send`">
                    <img src="/assets/arrow.svg" alt="Invia" class="size-full" />
                </button>
            </div>
        </form>
        <span v-if="counter" class="self-end px-1 text-[13px] leading-none" :class="counterClass">
            {{ counter.text }}
        </span>
    </div>
</template>

<style scoped>
@reference '#app.css';

/* The arrow asset carries its own blue rounded background, so the button is just a frame
   for it: no padding, no fill of its own. Sized to sit inside the field rather than match
   its height. */
.send-btn {
    @apply p-0! bg-transparent size-10 shrink-0;

    @variant disabled {
        @apply opacity-40;
    }
}
</style>
